<template>
  <div
    v-if="loaded"
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <timer-modal
      v-if="showTimer"
      :is-first-time="firstTime"
      :blur-question="blurQuestion"
      :flashcard="currentFlashcard"
      @timer-start="timerStart"
      @expired-modal-closed="expiredModalClosed"
      @before-timer-modal-open="beforeTimerModalOpen"
    />
    <cert-attachment
      :flashcard="currentFlashcard"
    />
    <flashcard-header
      :answered="answered"
      :breadcrumbs="breadcrumbs"
      :category="currentFlashcardCategory"
      :flashcard="currentFlashcard"
      :show-improve-question="true"
      :show-progress="true"
    />
    <flashcard
      :answered="answered"
      :answer-ids="answerIds"
      :blur="blur"
      :blur-question="blurQuestion"
      :disabled="blur"
      :correct="correct"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      @set-answer-ids="setAnswerIds"
      @set-raw-answer-data="setRawAnswerData"
    />
    <timer-modal
      v-if="showTimer && !blurQuestion"
      :is-first-time="firstTime"
      :flashcard="currentFlashcard"
      @timer-start="timerStart"
      @expired-modal-closed="expiredModalClosed"
      @before-timer-modal-open="beforeTimerModalOpen"
    />
    <flashcard-footer
      :allow-skipping="false"
      :answered="answered"
      :answer-ids="answerIds"
      :category="currentFlashcardCategory"
      :flashcard="currentFlashcard"
      :raw-answer-data="rawAnswerData"
      @answer="answerFlashcard"
      @next-flashcard="triggerNextFlashcard"
    />
  </div>
</template>

<script>
import Flashcard from '@components/flashcards/flashcard';
import FlashcardFooter from '@components/flashcards/footer';
import FlashcardHeader from '@components/flashcards/header';
import CertAttachment from '@components/certAttachment';
import TimerModal from '@components/modals/timer';

import CertificationMixin from '@/mixins/certifications';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import RateLimitMixin from '@/mixins/rate-limit';

export default {
  components: {
    CertAttachment, Flashcard, FlashcardFooter, FlashcardHeader, TimerModal,
  },
  mixins: [CertificationMixin, FlashcardViewMixin, GlobalMixin, RateLimitMixin],
  data() {
    return {
      blur: false,
      statePromise: null,
      loaded: false,
      showTimer: true,
    };
  },
  computed: {
    blurQuestion() {
      return !this.currentFlashcard?.aseShowQuestion;
    },
    breadcrumbs() {
      let userCertification = this.$store.getters['userCertifications/userCertifications'][this.$route.params.userCertificationId];
      let question = userCertification.questionsDelivered.find(qd => qd.id === this.$route.params.flashcardId) || {};
      let name = question.questionType === 'remedial' ? 'Second Chance Content' : 'Core ASE Content';
      return [{ name: name, route: {}}];
    },
    currentFlashcardCategory() {
      if (this.currentFlashcard.id) {
        let categories = this.currentFlashcard.categoryIds.map(id => this.$store.getters['categories/categories'][id]);
        let categoryId = this.$route.params.categoryId;

        // if flashcard is just in top level category
        let category = categories.find(c => c.id == categoryId);

        if (!category) {
          // this happens if we are studying from a top level category and flashcard belongs to child catgory
          let parent = this.$store.getters['categories/categories'][categoryId];
          let retVal;

          parent.descendants.forEach(id => {
            if (this.currentFlashcard.categoryIds.includes(id)) {
              retVal = this.$store.getters['categories/categories'][id];
              return;
            }
          });

          return retVal;
        } else {
          return category;
        }
      }
    },
    dashboard() {
      const lastDashboardIds = this.$store.getters['dashboards/lastVisitedId'];
      if (Array.isArray(lastDashboardIds) && lastDashboardIds.length > 0) {
        return this.$store.getters['dashboards/get'][lastDashboardIds[0]];
      }
      return this.$store.getters['dashboards/menuDefaultDashboard'];
    },
    firstTime() {
      let f = !this.$store.getters['userCertifications/userCertificationsList'].some(uc => uc.questionsAnswered.length > 1);
      return f || this.currentFlashcard?.aseShowQuestion;
    },
    nextFlashcardRoute() {
      if (this.nextFlashcard) {
        return {name: 'userCertifications#flashcard', params: {categoryId: this.$route.params.categoryId, userCertificationId: this.$route.params.userCertificationId, flashcardId: this.nextFlashcard}, query: this.currentQuery};
      } else {
        return { name: 'userCertifications#show', query: this.currentQuery };
      }
    },
  },
  watch: {
    'currentFlashcard.id': {
      immediate: true,
      handler: function(id) {
        this.resetFlashcard(id);
      },
    },
  },
  created() {
    this.$eventBus.$on('timer-end', this.timerEnd);
    let stateParams = ['flashcards'];
    this.statePromise = this.$store.dispatch('flashcardStates/fetchStates', stateParams);
    let params = {};

    if (this.$route.params.flashcardId) {
      params.flashcardId = this.$route.params.flashcardId;
    }

    params.userCertificationId = this.$route.params.userCertificationId;
    params.type = this.$route.query.type;
    params.rateLimited = this.rateLimitingEnabled;

    this.sessionPromise = this.$store.dispatch('flashcards/createFlashcardSession', params);
  },
  mounted() {
    const that = this;
    this.sessionPromise.then(flashcard => {
      if(flashcard) {
        /*
         * ASE certification specific
         */
        if (this.$route.params.userCertificationId) {
          // we need to make sure flashcard states have loaded
          this.statePromise.then(() => {
            let state = this.$store.getters['flashcardStates/flashcardStates'][`${flashcard.id}-category-${this.$route.params.categoryId}`];
            // if a state exists that has expired as the last flashcard state, create
            // an incorrect state and mark as answered, otherwise flashcard is in progress
            if (state) {
              this.showTimer = false;
              let userCertification = this.$store.getters['userCertifications/userCertifications'][this.$route.params.userCertificationId];
              let certification = this.$store.getters['certifications/certifications'][userCertification.certificationId];
              let timeExpired = Math.floor((Date.now() - state.mobileCreatedAt) / 1000);
              if (timeExpired > (certification.secondsPerQuestion + flashcard.secondsPerQuestionDelta)) {
                this.$router.replace({ name: 'userCertifications#flashcard', params: { categoryId: this.$route.params.categoryId, userCertificationId: this.$route.params.userCertificationId, flashcardId: flashcard.id }, query: this.currentQuery });
                this.$nextTick(function () {
                  this.timerEnd();
                });
              } else {
                this.$router.replace({ name: 'userCertifications#flashcard', params: { categoryId: this.$route.params.categoryId, userCertificationId: this.$route.params.userCertificationId, flashcardId: flashcard.id }, query: this.currentQuery });
                // not sure why this needs 2 nextTicks, but timer doesn't render until
                // route params are present, so we need a better way to handle this
                // event bus use is not ideal here
                this.$nextTick(function () {
                  this.$nextTick(function () {
                    this.$eventBus.$emit('timer-start', timeExpired);
                  });
                });
              }
            } else {
              this.$router.replace({ name: 'userCertifications#flashcard', params: { categoryId: this.$route.params.categoryId, userCertificationId: this.$route.params.userCertificationId, flashcardId: flashcard.id }, query: this.currentQuery });
              this.blur = true;
            }
            that.loaded = true;
          });
        /*
         * End ASE certification specific
         */
        }
      } else {
        this.$router.go(-1);
      }
    }).catch(e => {
      this.$router.go(-1);
      Promise.reject(e);
    });
  },
  beforeUnmount() {
    this.$eventBus.$off('timer-end');
  },
  methods: {
    answerFlashcard(time) {
      if (this.timer) {
        clearInterval(this.timer);
      }

      this.$eventBus.$emit('timer-stop');

      // Load next flashcard
      if (!this.$store.getters['flashcards/flashcards'][this.nextFlashcard] && this.nextFlashcard) {
        let params = { ids: this.nextFlashcardGroup };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }

      // Evaluate the answer
      const correct = this.evaluateAnswer(this.currentFlashcard, this.answerIds, this.rawAnswerData);
      this.correct = correct;
      const isQOTD = this.$route.path.includes('/qotd');
      this.$analytics.events.answeredFlashcard(this.$store, this.currentFlashcardCategory, this.currentFlashcard, this.answerIds, this.rawAnswerData, correct, isQOTD, this.dashboard?.name);

      let params = { timeInSeconds: this.time };

      params.flashcardStatusId = this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id;
      params.timeInSeconds = this.currentCertification.secondsPerQuestion + this.currentFlashcard.secondsPerQuestionDelta - time;
      this.createFlashcardState(params);
      this.answered = true;
    },
    triggerNextFlashcard() {
      const hasNextFlashcard = this.nextFlashcard;
      this.goToNextFlashcard();
      if (hasNextFlashcard) {
        this.showTimer = true;
        this.blur = true;
      }
    },
    timerEnd() {
      // to show 0 on timer
      this.$eventBus.$emit('timer-stop');

      let userCertification = this.$store.getters['userCertifications/userCertifications'][this.$route.params.userCertificationId];
      let certification = this.$store.getters['certifications/certifications'][userCertification.certificationId];

      const params = {
        flashcardStatusId: this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id,
        timeInSeconds: certification.secondsPerQuestion + this.currentFlashcard.secondsPerQuestionDelta,
      };
      this.createFlashcardState(params);
      this.answered = true;
      this.correct = this.evaluateAnswer(this.currentFlashcard, this.answerIds, this.rawAnswerData);
    },
    expiredModalClosed() {
      this.answered = true;
      this.blur = false;
    },
    timerStart() {
      this.showTimer = false;
      this.blur = false;
      this.createFlashcardState({
        answerIds: [],
        rawAnswerData: {},
        correct: false,
        timeInSeconds: 0,
      });
    },
    beforeTimerModalOpen() {
      this.blur = true;
    },
  },
};
</script>

<style lang='scss' scoped>
  .flashcard-view-container {
    @include flashcard-view;

    &.noscroll {
      overflow: hidden !important;
    }
  }
</style>
